import type { AppEnv } from "@mube/config"
import {
  DEFAULT_APP_VERSION,
  DEFAULT_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
  SENTRY_REPLAY_SESSION_SAMPLE_RATE,
} from "./constants.js"

export const SENTRY_MASK_ATTRIBUTE = "data-sentry-mask"

const SIGNED_URL_QUERY_PATTERN = /[?&](X-Amz-[^=&]+|sig(nature)?|token|expires?|exp)=/i

const EMBEDDED_URL_PATTERN = /https?:\/\/[^\s"'<>]+/gi

const AUTHORIZATION_KEY = "authorization"

const QUERY_START = "?"

const SENTRY_SCRUB_MAX_DEPTH = 10

export const SENTRY_SCRUB_PLACEHOLDER = "[scrubbed]"

const REPLAY_PERFORMANCE_TAG = "performanceSpan"

const REPLAY_URL_OP_PREFIXES = ["resource.", "navigation."]

export type SentryWebConfig = {
  readonly dsn: string | undefined
  readonly appEnv: AppEnv
  readonly appVersion: string | undefined
  readonly replayOnErrorSampleRate: number | undefined
}

export type SentryReplayOptions = {
  readonly maskAllText: boolean
  readonly maskAllInputs: boolean
  readonly blockAllMedia: boolean
  readonly beforeAddRecordingEvent: <T>(event: T) => T
}

export type SentryWebOptions = {
  readonly enabled: boolean
  readonly dsn: string | undefined
  readonly environment: AppEnv
  readonly release: string
  readonly sendDefaultPii: boolean
  readonly replaysSessionSampleRate: number
  readonly replaysOnErrorSampleRate: number
  readonly replay: SentryReplayOptions
}

export function sanitizeSentryUrl(value: string): string {
  return value.replace(EMBEDDED_URL_PATTERN, (match) => {
    const queryIndex = match.indexOf(QUERY_START)
    if (queryIndex === -1) {
      return match
    }
    if (SIGNED_URL_QUERY_PATTERN.test(match.slice(queryIndex)) === false) {
      return match
    }
    return match.slice(0, queryIndex)
  })
}

export function stripSentrySecrets<T>(value: T): T {
  return stripSentryValue(value, new WeakSet(), 0) as T
}

function stripSentryValue(value: unknown, ancestors: WeakSet<object>, depth: number): unknown {
  if (typeof value === "string") {
    return sanitizeSentryUrl(value)
  }
  if (value === null || typeof value !== "object") {
    return value
  }
  if (depth >= SENTRY_SCRUB_MAX_DEPTH) {
    return SENTRY_SCRUB_PLACEHOLDER
  }
  if (value instanceof Error) {
    value.message = sanitizeSentryUrl(value.message)
    return value
  }
  if (ancestors.has(value)) {
    return SENTRY_SCRUB_PLACEHOLDER
  }
  ancestors.add(value)
  try {
    if (Array.isArray(value)) {
      return value.map((entry) => stripSentryValue(entry, ancestors, depth + 1))
    }
    const cleaned: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value)) {
      if (key.toLowerCase() === AUTHORIZATION_KEY) {
        continue
      }
      cleaned[key] = stripSentryValue(entry, ancestors, depth + 1)
    }
    return cleaned
  } finally {
    ancestors.delete(value)
  }
}

export function filterReplayRecordingEvent<T>(event: T): T {
  if (typeof event !== "object" || event === null) {
    return event
  }
  const data = (event as { data?: unknown }).data
  if (typeof data !== "object" || data === null) {
    return event
  }
  const { tag, payload } = data as { tag?: unknown; payload?: unknown }
  if (tag !== REPLAY_PERFORMANCE_TAG || typeof payload !== "object" || payload === null) {
    return event
  }
  const op = (payload as { op?: unknown }).op
  if (
    typeof op !== "string" ||
    REPLAY_URL_OP_PREFIXES.some((prefix) => op.startsWith(prefix)) === false
  ) {
    return event
  }
  return stripSentrySecrets(event)
}

export function filterSentryEvent<T>(event: T): T {
  return stripSentrySecrets(event)
}

export function filterSentryBreadcrumb<T>(breadcrumb: T): T {
  return stripSentrySecrets(breadcrumb)
}

export function buildSentryWebOptions(config: SentryWebConfig): SentryWebOptions {
  return {
    enabled: config.dsn !== undefined,
    dsn: config.dsn,
    environment: config.appEnv,
    release: config.appVersion ?? DEFAULT_APP_VERSION,
    sendDefaultPii: false,
    replaysSessionSampleRate: SENTRY_REPLAY_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate:
      config.replayOnErrorSampleRate ?? DEFAULT_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
    replay: {
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
      beforeAddRecordingEvent: filterReplayRecordingEvent,
    },
  }
}
